import type { LicenceModel } from '@kimuno/api-sdk/lib/types/responses/Billing';
import { Box, Typography } from '@mui/material';
import _ from 'lodash';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { AsyncActionStatus } from '../../../actions/common.actionTypes';
import { PremiumPlanSelected } from '../../../ampli';
import { customSizes, palette, spaces } from '../../../assets/styles/themes/tokens';
import MasterLayout from '../../../components/AppLayout/MasterLayout';
import FAQ from '../../../components/Pricing/FAQ';
import PricingPackage from '../../../components/PricingPackage';
import {
	type AmpliPremiumPlanSelectedPayload,
	ampliSubscriptionType,
	externalLinks,
	meinUnterrichtSchulePackage,
	packageLicenceIds,
	routes,
	userType,
} from '../../../constants';
import { selectActiveLicences } from '../../../reducers/licenceModels.selectors';
import { getSubscriptionStatus, selectUser } from '../../../reducers/user.selectors';
import services from '../../../services';
import type { User } from '../../../types/User';
import useRouter from '../../../utils/useRouter';
import useScreenSize from '../../../utils/useScreenSize';
import { isLicenceIdCurrentUserPlan, orderOldPlans } from '../utils';

const PricingPage: React.FC = () => {
	const { t } = useTranslation();
	const { router } = useRouter();
	const { isLgScreen, isMdScreen } = useScreenSize();
	const isDesktopScreen = isLgScreen || isMdScreen;
	const user = useSelector(selectUser);
	const subscriptionStatus = useSelector(getSubscriptionStatus);
	const licenceModels = useSelector((state) => selectActiveLicences(state));
	const showSchoolPackage = user?.type === userType.kombi || user?.type === userType.document;
	const { meinUnterricht360ForDocumentUsers, meinUnterrichtProForTrainees } = packageLicenceIds;

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		if (user && user?.canUpgrade === false) {
			router.push(`/${routes.dashboard.root}`);
		}
	}, [user?.canUpgrade]);

	// SM-92 Phase 6: only users with a Stripe customer record belong on the
	// new `/upgrade-options` page (incl. basis-with-stripeCustomerId who are
	// re-subscribing). Basis users without that record keep the legacy Zuora
	// pricing page.
	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		if (user?.isStripeUser) {
			router.push(`/${routes.stripeUpgrade.root}`);
		}
	}, [user?.isStripeUser]);

	if (
		!subscriptionStatus ||
		![AsyncActionStatus.READY, AsyncActionStatus.NOTFOUND].includes(subscriptionStatus)
	) {
		return <></>;
	}

	const isLicencesDataReady = () => !_.isEmpty(licenceModels);
	let plans = Object.values(licenceModels)
		.filter(
			(licence) =>
				user?.type &&
				licence.allowedUserTypes?.includes(user.type) &&
				licence.licenceModelId !== meinUnterrichtProForTrainees
		)
		.filter((licence) => licence.newPricing !== true);

	plans = orderOldPlans(plans, user?.type);

	const sendPremiumPlanSelectedEvent = (licenceId: number, plan: LicenceModel) => {
		if (user) {
			const payload: AmpliPremiumPlanSelectedPayload = {
				'license id': licenceId,
				'subscription interval': `${plan.period}mo`,
				'subscription type':
					licenceId === meinUnterrichtProForTrainees
						? ampliSubscriptionType.REF
						: ampliSubscriptionType.TEACHER,
			};
			services.track.ampliEventTrack({
				event: new PremiumPlanSelected(payload),
			});
		}
	};

	const getSecondaryCta = (plan: LicenceModel) => {
		if (plan.refDiscount && user?.type === userType.basis) {
			const { refDiscount, licenceModelId } = plan;
			const query: { licenceId?: string; promoCode?: string } = {};

			if (refDiscount.type === 'licence') {
				query.licenceId = refDiscount.value;
			} else if (refDiscount.type === 'promoCode') {
				query.licenceId = licenceModelId.toString();
				query.promoCode = refDiscount.value;
			}
			return {
				label: `50% ${t('Trainee Discount')}`,
				onClick: () => {
					sendPremiumPlanSelectedEvent(Number(query.licenceId), plan);
				},
				href: `/${routes.upgradePayment.root}?${new URLSearchParams(query).toString()}`,
			};
		}

		return undefined;
	};

	const getPackageInfo = (plan: LicenceModel, user: User) => {
		const caption = plan.caption;
		const packageName = plan.name;
		const description = t(plan.description || '');
		const isMostPopular = plan.isMostPopular;
		let price = `${plan.price.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} € mtl.`;
		let duration = `${plan.period} ${t('month term')}`;
		let showBenefitsSummary = false;
		const benefits = plan.benefits || [];

		const primaryCta = {
			label: undefined,
			onClick: () => {
				sendPremiumPlanSelectedEvent(plan.licenceModelId, plan);
			},
			href: `/${routes.upgradePayment.root}?licenceId=${plan.licenceModelId.toString()}`,
		};

		let secondaryCta = getSecondaryCta(plan);

		const isCurrentPlan = isLicenceIdCurrentUserPlan(user?.type ?? 0, plan.licenceModelId);
		const packageHighlight = {
			enable: isMostPopular || isCurrentPlan,
			label:
				isMostPopular && user?.type !== userType.document ? t('Most popular') : t('Your current plan'),
			color:
				isMostPopular && user?.type !== userType.document ? ('success' as const) : ('primary' as const),
			borderColor:
				isMostPopular && user?.type !== userType.document ? palette.success.main : palette.primary.main,
		};

		if (
			user.type === userType.document &&
			plan.licenceModelId === meinUnterricht360ForDocumentUsers
		) {
			price = '+5€ mtl.';
			duration = t('Until the end of your subscription');
			primaryCta.label = t('Switch now');
			secondaryCta = undefined;
			showBenefitsSummary = true;
		}

		return {
			packageName,
			description,
			caption,
			isMostPopular,
			price,
			duration,
			packageHighlight,
			primaryCta,
			secondaryCta,
			isLicenceIdCurrentUserPlan: isCurrentPlan,
			showBenefitsSummary,
			benefits,
		};
	};

	return (
		<MasterLayout forceNewNavigation sidebarCollapsed>
			<Box
				data-testid="pricing-page-container"
				sx={{
					backgroundColor: palette.common.white,
				}}
			>
				<Box data-testid="pricing-table-container">
					<Box
						data-testid="pricing-title-container"
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: spaces.md,
							paddingY: spaces.xxl,
						}}
					>
						<Typography variant="h5" sx={{ textAlign: 'center', color: palette.text.primary }}>
							{t('Find the right plan for your goals')}
						</Typography>
						<Typography variant="subtitle1" sx={{ textAlign: 'center', color: palette.text.secondary }}>
							{t('Tens of thousands of teachers and hundreds of schools already trust us')}
						</Typography>
					</Box>
					<Box
						sx={{
							display: 'flex',
							flexDirection: isDesktopScreen ? 'row' : 'column',
							justifyContent: 'center',
							alignItems: isDesktopScreen ? 'normal' : 'center',
							gap: isDesktopScreen ? spaces.md : spaces.xl,
						}}
					>
						{isLicencesDataReady() &&
							user &&
							// biome-ignore lint/suspicious/noArrayIndexKey: index keys are pre-existing; switching the key source changes reconciliation behavior
							plans.map((plan, index) => <PricingPackage key={index} {...getPackageInfo(plan, user)} />)}
						{showSchoolPackage && (
							<PricingPackage
								packageName={meinUnterrichtSchulePackage.name}
								description={t(meinUnterrichtSchulePackage.description)}
								price={t(meinUnterrichtSchulePackage.price)}
								duration={t(meinUnterrichtSchulePackage.duration)}
								packageHighlight={{ enable: false }}
								isLicenceIdCurrentUserPlan={false}
								primaryCta={{
									label: t('Request an offer now'),
									href: externalLinks.schoolLicenceOffer,
								}}
								benefits={meinUnterrichtSchulePackage.benefits}
								isMostPopular={false}
							/>
						)}
					</Box>
				</Box>
				<Box
					data-testid="faq-container"
					sx={{
						width: isDesktopScreen
							? customSizes.pricing.faq.width.desktop
							: customSizes.pricing.faq.width.mobile,
						margin: '0 auto',
						paddingTop: spaces.xl,
						paddingBottom: spaces.xl,
					}}
				>
					<FAQ />
				</Box>
			</Box>
		</MasterLayout>
	);
};

export default PricingPage;
